import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { navigateToFlowComposerPrompt } from '@/lib/flow-composer-handoff';
import { type SetupItem, connectionsOf } from '@/lib/company-setup';
import { ApplyPanel } from '@/components/profile/setup/ApplyPanel';
import type { AppProposal, UsagePlace, UsageProposal } from '@shared/types/company-qualification';

/** Where a usage is done in Domino (also a ritual's « Ouvrir » in Aujourd'hui). */
export const PLACE_ROUTES: Partial<Record<UsagePlace, { to: string; label: string }>> = {
  today: { to: '/tasks', label: 'Ouvrir Mon travail' },
  accounts: { to: '/accounts', label: 'Ouvrir Comptes' },
  apps: { to: '/tools', label: 'Ouvrir Applications' },
  objectives: { to: '/profil?tab=objectives', label: 'Ouvrir les objectifs' },
  forge: { to: '/forge', label: 'Ouvrir la Forge' },
  chat: { to: '/tasks', label: 'Ouvrir Mon travail' },
  profile: { to: '/profil', label: 'Ouvrir le Profil' },
};

/** What the Forge receives: the need as the qualification measured it. */
export const forgeRequest = (app: AppProposal): string => [
  `Je veux une application « ${app.title} ».`,
  `Le goulot : ${app.bottleneck}`,
  app.cost ? `Ce que ça coûte aujourd’hui : ${app.cost}` : '',
  `Comment on mesure : ${app.measure}`,
  app.truthSet ? `On la jugera sur : ${app.truthSet}` : '',
  app.expected ? `Résultat attendu : ${app.expected}` : '',
].filter(Boolean).join('\n');

/** What the Forge receives for a reflex: what Domino should do, and at which moment. */
export const reflexRequest = (usage: UsageProposal): string => [
  `Je veux que Domino s’en charge au bon moment : ${usage.what}`,
  `Pour : ${usage.who}`,
].join('\n');

const linkClass = 'inline-flex h-8 items-center rounded-lg border border-domino-cardBorder px-3 text-sm text-domino-ink hover:bg-domino-muted';

/** The button that takes a line to where it is done. */
export const SetupAction = ({ item, connected, onApplied }: { item: SetupItem; connected: ReadonlySet<string>; onApplied: () => void }) => {
  const navigate = useNavigate();
  const [applying, setApplying] = useState(false);
  const { source } = item;
  if (source.list === 'apps') {
    return <Button size="sm" variant="secondary" onClick={() => navigateToFlowComposerPrompt(navigate, forgeRequest(source.entry))}>Créer dans la Forge</Button>;
  }
  if (source.list === 'usage' && item.kind === 'reflex') {
    return <Button size="sm" variant="secondary" onClick={() => navigateToFlowComposerPrompt(navigate, reflexRequest(source.entry))}>Créer dans la Forge</Button>;
  }
  if (source.list === 'usage') {
    const route = PLACE_ROUTES[source.entry.place];
    return route ? <Link to={route.to} className={linkClass}>{route.label}</Link> : null;
  }
  const { area } = source.entry;
  if (area === 'profile') {
    return (
      <div className="w-full">
        {!applying && <Button size="sm" variant="secondary" onClick={() => setApplying(true)}>Préparer la mise à jour du Profil</Button>}
        {applying && <ApplyPanel proposalId={item.id} onApplied={() => { setApplying(false); onApplied(); }} onCancel={() => setApplying(false)} />}
      </div>
    );
  }
  if (area === 'connections') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/connections" className={linkClass}>Ouvrir Connexions</Link>
        {connectionsOf(source.entry.text, connected).map((provider) => (
          <span key={provider.provider} className={cn('rounded-full px-2 py-0.5 text-xs', provider.connected ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-100 text-stone-600')}>
            {provider.label} {provider.connected ? 'connecté' : 'à connecter'}
          </span>
        ))}
      </div>
    );
  }
  if (area === 'documents') return <Link to="/profil?tab=documents" className={linkClass}>Ouvrir Documents</Link>;
  return null;
};
